import Button, { ButtonType } from "components/Elements/Buttonv2";
import Modal from "components/Elements/Modalv2";
import React, { FC } from "react";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ApiService from "services/api.service";

interface DeleteWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  workflowId?: string;
}

const DeleteWorkflowModal: FC<DeleteWorkflowModalProps> = ({
  isOpen,
  onClose,
  workflowId,
}) => {
  const queryClient = useQueryClient();

  // Optimistically remove the workflow from every cached page (matching the
  // other list deletes), then roll back if the request fails.
  const deleteMutation = useMutation({
    // Take the id as a variable, not from the prop, so closing the dialog in
    // onMutate (which clears the prop) can't change what gets deleted.
    mutationFn: (id: string) =>
      ApiService.patch({ url: "/workflows/delete/" + id }),
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: ["workflows"] });
      const snapshot = queryClient.getQueriesData({ queryKey: ["workflows"] });
      queryClient.setQueriesData<{
        rows: { id: string }[];
        totalPages: number;
      }>({ queryKey: ["workflows"] }, (old) =>
        old ? { ...old, rows: old.rows.filter((r) => r.id !== id) } : old
      );
      // The row is already gone, so close the dialog immediately.
      onClose();
      return { snapshot };
    },
    onError: (_err, _id, context) => {
      context?.snapshot?.forEach(([key, value]) =>
        queryClient.setQueryData(key, value)
      );
      toast.error("Failed to delete the workflow");
    },
  });

  const isDeleting = deleteMutation.isPending;

  const handleDeleteWorkflow = () => {
    if (!workflowId || isDeleting) return;
    deleteMutation.mutate(workflowId);
  };

  return (
    <Modal isOpen={isOpen} onClose={isDeleting ? () => {} : onClose}>
      <div className="font-roboto font-medium text-[16px] text-themed-text leading-[24px]">
        Are you sure to delete the workflow?
      </div>
      <div className="mt-[8px]">
        Please note that deleting the workflow is an irreversible action.
      </div>

      <div className="mt-[24px] flex items-center justify-end gap-2 font-roboto font-normal text-[14px] text-themed-text leading-[22px]">
        <Button
          type={ButtonType.SECONDARY}
          onClick={onClose}
          disabled={isDeleting}
        >
          Cancel
        </Button>
        <Button
          type={ButtonType.DANGEROUS}
          onClick={handleDeleteWorkflow}
          disabled={!workflowId || isDeleting}
        >
          {isDeleting ? "Deleting…" : "Delete"}
        </Button>
      </div>
    </Modal>
  );
};

export default DeleteWorkflowModal;
